/**
 * The one geometry module (SPEC §5.1, §5.4): layout slot → OBS scene-item transform,
 * and transform → where OBS actually draws the video.
 *
 * Used by the builder preview and by the graphics app (OBS apply + the GFX Cameras
 * surface that draws frames and nameplates), so frames and video always line up.
 *
 * `computeItemGeometry` is a port of libobs `update_item_transform`,
 * `calculate_bounds_data`, `calc_cx/calc_cy` and `add_alignment`
 * (`libobs/obs-scene.c`, OBS 32.2.2), including its float32 arithmetic, `(int)`
 * truncations, integer halving and `roundf`. Flips (negative scale) are not used by
 * the suite and are rejected.
 */
import { REFERENCE_CANVAS, type LayoutFile, type LayoutSlot } from './layout.ts';
import { ObsAlignment, type ObsBoundsType, type ObsSceneItemTransformInput } from './obs.ts';

/** Compile-time check: every compiled transform is a valid SetSceneItemTransform payload. */
export type _CompiledIsObsInput = GeometryTransform extends ObsSceneItemTransformInput ? true : never;

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Crop {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** Four corners, clockwise from the local top-left. */
export type Quad = readonly [Point, Point, Point, Point];

/**
 * Source size of every camera slot. Slots show nested scenes (`CAM-n`, `SHARE`), and a
 * nested scene's size is the canvas; the CoreVideo input fills it (SPEC §4).
 */
export const CAMERA_SOURCE_SIZE: Readonly<Size> = REFERENCE_CANVAS;

/** The transform fields geometry depends on (all required). */
export interface GeometryTransform {
  positionX: number;
  positionY: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  alignment: number;
  boundsType: ObsBoundsType;
  boundsAlignment: number;
  boundsWidth: number;
  boundsHeight: number;
  cropLeft: number;
  cropRight: number;
  cropTop: number;
  cropBottom: number;
  cropToBounds: boolean;
}

/** Where OBS draws a scene item. Local rects are relative to `position`, before rotation. */
export interface ItemGeometry {
  position: Point;
  /** Degrees, clockwise (OBS and CSS agree). */
  rotation: number;
  /** Scale OBS applies to the cropped source texture. */
  scale: Point;
  /** Crop OBS applies to the source, in source pixels: user crop + crop-to-bounds crop. */
  sourceCrop: Crop;
  /** OBS's item box (the red selection box): the bounds box, or the scaled source. */
  box: Rect;
  /** The visible video. */
  content: Rect;
  /** `box` and `content` corners on the canvas, rotation applied. */
  boxQuad: Quad;
  contentQuad: Quad;
  /** Axis-aligned canvas bounds of the quads (equal to the rects when rotation is 0). */
  boxBounds: Rect;
  contentBounds: Rect;
}

const f = Math.fround;

/** C `(int)` / `(uint32_t)` cast of a float: truncation toward zero. */
const toInt = (v: number): number => Math.trunc(v);

/** C `roundf`: half away from zero. */
const roundf = (v: number): number => Math.sign(v) * Math.round(Math.abs(v));

/** libobs `add_alignment` (integer halving for centered axes). */
function addAlignment(v: Point, align: number, cx: number, cy: number): void {
  if (align & ObsAlignment.Right) v.x = f(v.x + cx);
  else if ((align & ObsAlignment.Left) === 0) v.x = f(v.x + Math.trunc(cx / 2));
  if (align & ObsAlignment.Bottom) v.y = f(v.y + cy);
  else if ((align & ObsAlignment.Top) === 0) v.y = f(v.y + Math.trunc(cy / 2));
}

/** libobs `calc_cx` / `calc_cy`: source size after crop, never below 2. */
function cropped(size: number, a: number, b: number): number {
  const c = a + b;
  return c > size ? 2 : size - c;
}

function cropToBoundsApplies(t: GeometryTransform): boolean {
  return (
    t.cropToBounds &&
    (t.boundsType === 'OBS_BOUNDS_SCALE_OUTER' ||
      t.boundsType === 'OBS_BOUNDS_SCALE_TO_HEIGHT' ||
      t.boundsType === 'OBS_BOUNDS_SCALE_TO_WIDTH')
  );
}

function rotate(p: Point, degrees: number, about: Point): Point {
  if (degrees === 0) return { x: p.x + about.x, y: p.y + about.y };
  const r = (degrees * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return { x: p.x * cos - p.y * sin + about.x, y: p.x * sin + p.y * cos + about.y };
}

function quadOf(rect: Rect, rotation: number, position: Point): Quad {
  const { x, y, width: w, height: h } = rect;
  return [
    rotate({ x, y }, rotation, position),
    rotate({ x: x + w, y }, rotation, position),
    rotate({ x: x + w, y: y + h }, rotation, position),
    rotate({ x, y: y + h }, rotation, position),
  ];
}

function boundsOf(q: Quad): Rect {
  const xs = q.map((p) => p.x);
  const ys = q.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

/**
 * Computes where OBS draws an item with transform `t` and source `source`.
 * @throws RangeError for negative or zero scale (flips are not supported here).
 */
export function computeItemGeometry(t: GeometryTransform, source: Size = CAMERA_SOURCE_SIZE): ItemGeometry {
  if (!(t.scaleX > 0) || !(t.scaleY > 0)) throw new RangeError('geometry: scaleX/scaleY must be > 0 (flips are not supported)');
  const srcW = toInt(source.width);
  const srcH = toInt(source.height);

  const boundsCrop: Crop = { left: 0, right: 0, top: 0, bottom: 0 };
  // Pre-bounds cropped size (`width`/`height` in update_item_transform).
  const width = cropped(srcW, t.cropLeft, t.cropRight);
  const height = cropped(srcH, t.cropTop, t.cropBottom);
  let cx = width;
  let cy = height;
  const origin: Point = { x: 0, y: 0 };
  const scale: Point = { x: f(t.scaleX), y: f(t.scaleY) };
  const hasBounds = t.boundsType !== 'OBS_BOUNDS_NONE';
  const bw = f(t.boundsWidth);
  const bh = f(t.boundsHeight);

  if (hasBounds) {
    // calculate_bounds_data
    let w = f(cx * Math.abs(scale.x));
    let h = f(cy * Math.abs(scale.y));
    const itemAspect = f(w / h);
    const boundsAspect = f(bw / bh);
    let type = t.boundsType;
    if (t.boundsType === 'OBS_BOUNDS_MAX_ONLY' && (w > bw || h > bh)) type = 'OBS_BOUNDS_SCALE_INNER';

    if (type === 'OBS_BOUNDS_SCALE_INNER' || type === 'OBS_BOUNDS_SCALE_OUTER') {
      let useWidth = boundsAspect < itemAspect;
      if (t.boundsType === 'OBS_BOUNDS_SCALE_OUTER') useWidth = !useWidth;
      const mul = useWidth ? f(bw / w) : f(bh / h);
      scale.x = f(scale.x * mul);
      scale.y = f(scale.y * mul);
    } else if (type === 'OBS_BOUNDS_SCALE_TO_WIDTH') {
      const mul = f(bw / w);
      scale.x = f(scale.x * mul);
      scale.y = f(scale.y * mul);
    } else if (type === 'OBS_BOUNDS_SCALE_TO_HEIGHT') {
      const mul = f(bh / h);
      scale.x = f(scale.x * mul);
      scale.y = f(scale.y * mul);
    } else if (type === 'OBS_BOUNDS_STRETCH') {
      scale.x = f(bw / cx);
      scale.y = f(bh / cy);
    }

    w = f(cx * scale.x);
    h = f(cy * scale.y);
    const widthDiff = f(bw - Math.abs(w));
    const heightDiff = f(bh - Math.abs(h));
    cx = toInt(bw);
    cy = toInt(bh);
    addAlignment(origin, t.boundsAlignment, toInt(-widthDiff), toInt(-heightDiff));

    if (cropToBoundsApplies(t) && (widthDiff < -0.1 || heightDiff < -0.1)) {
      const cropWidth = widthDiff < -0.1;
      const diff = cropWidth ? widthDiff : heightDiff;
      const cropScale = cropWidth ? scale.x : scale.y;
      const mask = cropWidth ? ObsAlignment.Left | ObsAlignment.Right : ObsAlignment.Top | ObsAlignment.Bottom;
      const cropAlign = t.boundsAlignment & mask;
      const overdraw = f(Math.abs(f(diff / cropScale)));
      const overdrawTl =
        cropAlign & (ObsAlignment.Top | ObsAlignment.Left) ? 0 : cropAlign & (ObsAlignment.Bottom | ObsAlignment.Right) ? overdraw : f(overdraw / 2);
      const overdrawBr = f(overdraw - overdrawTl);
      const cropBr = toInt(roundf(overdrawBr));
      const cropTl = toInt(roundf(overdrawTl));
      if (cropWidth) {
        boundsCrop.right = cropBr;
        boundsCrop.left = cropTl;
        origin.x = 0;
      } else {
        boundsCrop.bottom = cropBr;
        boundsCrop.top = cropTl;
        origin.y = 0;
      }
    }
  } else {
    cx = toInt(f(cx * scale.x));
    cy = toInt(f(cy * scale.y));
  }

  addAlignment(origin, t.alignment, cx, cy);

  // The drawn texture is the source minus crop and bounds crop.
  const texW = cropped(srcW, t.cropLeft + boundsCrop.left, t.cropRight + boundsCrop.right);
  const texH = cropped(srcH, t.cropTop + boundsCrop.top, t.cropBottom + boundsCrop.bottom);
  const content: Rect = { x: -origin.x, y: -origin.y, width: f(texW * scale.x), height: f(texH * scale.y) };

  // box_transform
  const boxW = hasBounds ? bw : f(t.scaleX * width);
  const boxH = hasBounds ? bh : f(t.scaleY * height);
  const baseOrigin: Point = { x: 0, y: 0 };
  addAlignment(baseOrigin, t.alignment, toInt(boxW), toInt(boxH));
  const box: Rect = { x: -baseOrigin.x, y: -baseOrigin.y, width: boxW, height: boxH };

  const position: Point = { x: f(t.positionX), y: f(t.positionY) };
  const boxQuad = quadOf(box, t.rotation, position);
  const contentQuad = quadOf(content, t.rotation, position);
  return {
    position,
    rotation: t.rotation,
    scale,
    sourceCrop: {
      left: t.cropLeft + boundsCrop.left,
      right: t.cropRight + boundsCrop.right,
      top: t.cropTop + boundsCrop.top,
      bottom: t.cropBottom + boundsCrop.bottom,
    },
    box,
    content,
    boxQuad,
    contentQuad,
    boxBounds: boundsOf(boxQuad),
    contentBounds: boundsOf(contentQuad),
  };
}

/** Values the compiler fixes for every slot (not part of the layout file). */
export const COMPILED_SLOT_CONSTANTS = {
  scaleX: 1,
  scaleY: 1,
  /** Centered within the bounds box. */
  boundsAlignment: 0,
  /** `SCALE_OUTER` fills the box and crops the overflow instead of spilling outside it. */
  cropToBounds: true,
} as const;

/**
 * The transform the graphics app sends with `SetSceneItemTransform` for a slot.
 *
 * Position and bounds are rounded to whole pixels: a real OBS 32.2.2 stores fractional
 * `positionY`/`boundsHeight` snapped to 0.5 px (371.25 came back as 371.5), so an
 * unrounded value would put the drawn frame up to half a pixel off the video. Both OBS
 * and the frame geometry use this rounded transform.
 */
export function compileSlotTransform(slot: LayoutSlot): GeometryTransform {
  return {
    positionX: Math.round(slot.positionX),
    positionY: Math.round(slot.positionY),
    rotation: slot.rotation,
    alignment: slot.alignment,
    boundsType: slot.boundsType,
    boundsWidth: Math.round(slot.boundsWidth),
    boundsHeight: Math.round(slot.boundsHeight),
    cropLeft: slot.cropLeft,
    cropRight: slot.cropRight,
    cropTop: slot.cropTop,
    cropBottom: slot.cropBottom,
    ...COMPILED_SLOT_CONSTANTS,
  };
}

/** Where OBS draws a slot's camera (source: a 1920×1080 nested scene). */
export function slotGeometry(slot: LayoutSlot, source: Size = CAMERA_SOURCE_SIZE): ItemGeometry {
  return computeItemGeometry(compileSlotTransform(slot), source);
}

/**
 * Inverse for editing: the position that puts an unrotated item box of `rect` at
 * `rect.x/y` with `alignment`, using OBS's integer alignment offsets.
 */
export function boxToPosition(rect: Rect, alignment: number): Point {
  const offset: Point = { x: 0, y: 0 };
  addAlignment(offset, alignment, toInt(rect.width), toInt(rect.height));
  return { x: rect.x + offset.x, y: rect.y + offset.y };
}

/** Which corner/edge-midpoint of the nameplate box sits at `point`. */
export interface NameplatePlacement {
  point: Point;
  horizontal: 'left' | 'center';
  vertical: 'top' | 'bottom';
}

/**
 * Turns a slot's nameplate hint into a canvas point against the visible video
 * (`contentBounds`). Returns `null` when the slot has no hint: the bundle decides.
 * The bundle owns the nameplate's look and padding; this only says where (SPEC §5.2).
 */
export function nameplatePlacement(slot: LayoutSlot, geometry: ItemGeometry = slotGeometry(slot)): NameplatePlacement | null {
  if (!slot.nameplate) return null;
  const r = geometry.contentBounds;
  const dx = slot.nameplate.offsetX ?? 0;
  const dy = slot.nameplate.offsetY ?? 0;
  const centerX = r.x + r.width / 2;
  switch (slot.nameplate.anchor) {
    case 'inside-bottom-left':
      return { point: { x: r.x + dx, y: r.y + r.height + dy }, horizontal: 'left', vertical: 'bottom' };
    case 'inside-bottom-center':
      return { point: { x: centerX + dx, y: r.y + r.height + dy }, horizontal: 'center', vertical: 'bottom' };
    case 'below':
      return { point: { x: centerX + dx, y: r.y + r.height + dy }, horizontal: 'center', vertical: 'top' };
    case 'above':
      return { point: { x: centerX + dx, y: r.y + dy }, horizontal: 'center', vertical: 'bottom' };
  }
}

/** Slot IDs bottom → top: by `zIndex`, then `order`, then `slotId`. */
export function stackingOrder(layout: Pick<LayoutFile, 'slots'>): string[] {
  return [...layout.slots]
    .sort((a, b) => a.zIndex - b.zIndex || a.order - b.order || a.slotId.localeCompare(b.slotId))
    .map((s) => s.slotId);
}

/** One slot, compiled for OBS and for drawing. */
export interface CompiledSlot {
  slotId: string;
  /** `SetSceneItemEnabled`: hidden (parked) slots are disabled. */
  enabled: boolean;
  /** Send as `sceneItemTransform`; a valid `ObsSceneItemTransformInput`. */
  transform: GeometryTransform;
  geometry: ItemGeometry;
  nameplate: NameplatePlacement | null;
}

/** Compiles a whole layout: per-slot transforms and geometry, plus the stacking order. */
export function compileLayout(layout: LayoutFile, source: Size = CAMERA_SOURCE_SIZE): { slots: CompiledSlot[]; stacking: string[] } {
  return {
    slots: layout.slots.map((slot) => {
      const transform = compileSlotTransform(slot);
      const geometry = computeItemGeometry(transform, source);
      return { slotId: slot.slotId, enabled: slot.hidden !== true, transform, geometry, nameplate: nameplatePlacement(slot, geometry) };
    }),
    stacking: stackingOrder(layout),
  };
}
